import { useRef } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { DATA_TOPIC_LABELS, Survey, DataTopicStatus } from "@/services/surveysListService"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FileText, Check, MessageSquareText, Pencil, X, ExternalLink } from "lucide-react"
import { Link } from "react-router-dom"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AddReportDialog from "@/components/survey-catalogue/AddReportDialog.tsx"
import UpdateReportDialog from "@/components/survey-catalogue/UpdateReportDialog"
import { useSurveySort, SortableKey } from "@/hooks/useSurveySort"
import SortableColumnHeader from "@/components/survey-catalogue/SortableColumnHeader"
import TruncatedTextCell from "@/components/shared/TruncatedTextCell"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import {
  useInlineDataTopicEdit,
  DATA_TOPIC_FIELDS,
  DATA_TOPIC_OPTIONS,
} from "@/hooks/useInlineDataTopicEdit"

/**
 * Estimated height (in px) of a single rendered table row.
 */
const ESTIMATED_ROW_HEIGHT_PX: number = 49

/**
 * Number of extra rows rendered above and below the visible viewport.
 */
const ROW_OVERSCAN: number = 25

const baseColumns: { key: keyof Survey; label: string }[] = [
  { key: "mapSurveyId", label: "MAP Survey ID" },
  { key: "countryName", label: "Country" },
  { key: "year", label: "Year" },
  { key: "surveyType", label: "Type" },
  { key: "populationRepresentativeness", label: "Targeted Population" },
  { key: "surveyStatus", label: "Status" },
  { key: "reportBiblioFile", label: "Report" },
]

/**
 * Column groups for the compact view, consumed by the parent interface to
 * drive the "Manage columns" dialog and the default set of visible columns.
 *
 * The compact table collapses all individual data topic columns into a single
 * "Data Topics" column, so this list differs from the expanded view's groups.
 */
export const columnGroups: { keys: (keyof Survey)[]; label: string }[] = [
  { keys: ["mapSurveyId"], label: "MAP Survey ID" },
  { keys: ["countryName"], label: "Country" },
  { keys: ["year"], label: "Year" },
  { keys: ["surveyType"], label: "Type" },
  { keys: ["populationRepresentativeness"], label: "Targeted Population" },
  { keys: ["surveyStatus"], label: "Status" },
  { keys: ["reportBiblioFile"], label: "Report" },
  { keys: ["dataTopics"], label: "Data Topics" },
]

const sectionDividers = new Set<keyof Survey>(["reportBiblioFile"])

// const codeButtonsDisabled: boolean = true

const CELL_HORIZONTAL_PADDING_PX: number = 16

/**
 * Renders the Report cell content based on column key, value, editing state and survey.
 *
 * @param key - The Survey property key for this column.
 * @param value - The value of the survey property.
 * @param survey - The full survey object.
 * @param onAddReportFile - Callback invoked after a report is successfully added.
 * @param isEditing - Whether this row is currently in inline edit mode.
 * @returns The rendered cell content.
 */
function renderBaseCell(
  key: keyof Survey,
  value: Survey[keyof Survey],
  survey: Survey,
  onAddReportFile: () => void,
  isEditing: boolean = false
) {
  if (key === "surveyStatus") {
    return <TruncatedCell value={value != null ? String(value) : null} />
  }
  if (key === "reportBiblioFile") {
    if (!value) return <AddReportDialog onReportCreated={onAddReportFile} survey={survey} />
    if (isEditing) return <UpdateReportDialog onReportCreated={onAddReportFile} survey={survey} />
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a href={String(value)} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="icon" className="size-7">
              <FileText />
            </Button>
          </a>
        </TooltipTrigger>
        <TooltipContent>Open survey report file in new tab</TooltipContent>
      </Tooltip>
    )
  }
  // Render undefined values as blank cells rather than the string "undefined".
  if (value === undefined || value === null) return null

  if (typeof value === "boolean") return value ? "Yes" : "No"
  return String(value)
}

interface SurveyCompactTableProps {
  surveys: Survey[]
  visibleKeys: Set<keyof Survey>
  /** Callback invoked after a survey's data topics are updated. */
  onSurveyUpdated: () => Promise<void>
  /** Callback invoked when the user clicks the notes button for a survey. */
  onViewNotes: (survey: Survey) => void
  /** Callback invoked when the user adds a report to a survey. */
  onAddReportFile: () => void
  /** Mapping of survey type codes to their human-readable descriptions. */
  surveyTypeLabels: Record<string, string>
}

/** Set of column keys that support sorting via click-to-sort headers. */
const sortableColumns = new Set<keyof Survey>(["year", "surveyType"])

const SurveyCompactTable = ({
  surveys,
  visibleKeys,
  onSurveyUpdated,
  onViewNotes,
  onAddReportFile,
  surveyTypeLabels,
}: SurveyCompactTableProps) => {
  const { sortedSurveys, sortConfig, toggleSort, getRowKey } = useSurveySort(surveys)
  const {
    editingRowKey,
    formValues,
    submitting,
    submitError,
    openEdit,
    closeEdit,
    handleFieldChange,
    handleSave,
  } = useInlineDataTopicEdit(onSurveyUpdated)
  const visibleBaseColumns = baseColumns.filter(c => visibleKeys.has(c.key))
  const showDataTopics = visibleKeys.has("dataTopics")

  const isMapSurveyIdVisible = visibleKeys.has("mapSurveyId")
  const isCountryNameVisible = visibleKeys.has("countryName")

  const mapSurveyIdWidth = 160 // px
  const countryNameWidth = 160 // px

  const totalColumnCount: number = visibleBaseColumns.length + (showDataTopics ? 1 : 0) + 1

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: sortedSurveys.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
    overscan: ROW_OVERSCAN,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const totalSize: number = rowVirtualizer.getTotalSize()

  const paddingTop: number = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom: number =
    virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0

  return (
    <div className="relative flex-1 min-h-0">
      {/* Saving overlay */}
      {submitting && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm">
          <LoadingSpinner loadingContent="Saving changes..." variant="outline" size="sm" />
        </div>
      )}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 h-full overflow-auto border border-border/50 [&>[data-slot=table-container]]:overflow-x-visible"
      >
      <Table className="relative border-separate border-spacing-0 min-w-max">
        <TableHeader className="sticky top-0 z-30 bg-card shadow-sm">
          <TableRow>
            {visibleBaseColumns.map(col => {
              const isSticky = col.key === "mapSurveyId" || col.key === "countryName"
              let leftStyle: React.CSSProperties = {}
              if (col.key === "mapSurveyId") {
                leftStyle = {
                  left: 0,
                  width: `${mapSurveyIdWidth}px`,
                  minWidth: `${mapSurveyIdWidth}px`,
                  maxWidth: `${mapSurveyIdWidth}px`,
                }
              } else if (col.key === "countryName") {
                leftStyle = {
                  left: isMapSurveyIdVisible ? `${mapSurveyIdWidth}px` : 0,
                  width: `${countryNameWidth}px`,
                  minWidth: `${countryNameWidth}px`,
                  maxWidth: `${countryNameWidth}px`,
                }
              }

              const isRightmostSticky =
                col.key === "countryName" || (col.key === "mapSurveyId" && !isCountryNameVisible)

              return (
                <TableHead
                  key={col.key}
                  style={isSticky ? leftStyle : undefined}
                  className={
                    [
                      "cursor-default",
                      sectionDividers.has(col.key) ? "border-l" : "",
                      isSticky ? "md:sticky md:z-30 bg-card" : "",
                      isRightmostSticky
                        ? "md:border-r"
                        : "",
                      col.key === "surveyType" ? "max-w-16" : "",
                    ]
                      .join(" ")
                      .trim() || undefined
                  }
                >
                  {sortableColumns.has(col.key) ? (
                    <SortableColumnHeader
                      columnKey={col.key as SortableKey}
                      label={col.label}
                      sortConfig={sortConfig}
                      onToggleSort={toggleSort}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              )
            })}
            {showDataTopics && <TableHead className="border-l">Data Topics Extracted</TableHead>}
            <TableHead className="bg-card md:sticky right-0 md:z-30 border-l" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedSurveys.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={totalColumnCount}
                className="text-center text-muted-foreground py-8"
              >
                No surveys found.
              </TableCell>
            </TableRow>
          ) : (
            <>
              {paddingTop > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={totalColumnCount} style={{ height: `${paddingTop}px` }} />
                </tr>
              )}
              {virtualRows.map(virtualRow => {
                const survey = sortedSurveys[virtualRow.index]
                const rowKey = getRowKey(survey)
                const isEditing = editingRowKey === rowKey
                const isDimmed = editingRowKey !== null && !isEditing
                return (
                  <TableRow
                    key={rowKey}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    className={[
                      "transition-colors",
                      isDimmed ? "pointer-events-none" : "hover:bg-accent/30",
                      isEditing ? "bg-accent/20" : "",
                    ].join(" ")}
                  >
                    {visibleBaseColumns.map(col => {
                      const isSticky = col.key === "mapSurveyId" || col.key === "countryName"
                      let leftStyle: React.CSSProperties = {}
                      if (col.key === "mapSurveyId") {
                        leftStyle = {
                          left: 0,
                          width: `${mapSurveyIdWidth}px`,
                          minWidth: `${mapSurveyIdWidth}px`,
                          maxWidth: `${mapSurveyIdWidth}px`,
                        }
                      } else if (col.key === "countryName") {
                        leftStyle = {
                          left: isMapSurveyIdVisible ? `${mapSurveyIdWidth}px` : 0,
                          width: `${countryNameWidth}px`,
                          minWidth: `${countryNameWidth}px`,
                          maxWidth: `${countryNameWidth}px`,
                        }
                      }

                      const isRightmostSticky =
                        col.key === "countryName" ||
                        (col.key === "mapSurveyId" && !isCountryNameVisible)

                      return (
                        <TableCell
                          key={col.key}
                          style={isSticky ? leftStyle : undefined}
                          className={
                            [
                              "cursor-default",
                              sectionDividers.has(col.key) ? "border-l" : "",
                              isSticky ? "md:sticky md:z-10 bg-card border-b" : "border-b",
                              isDimmed && !isSticky ? "opacity-30" : "",
                              isRightmostSticky ? "md:border-r border-b " : "",
                              col.key === "surveyType" ? "max-w-16" : "",
                              col.key === "reportBiblioFile" ? "text-center w-[60px]" : "",
                              col.key === "surveyStatus" ? "max-w-[150px]" : "",
                            ]
                              .join(" ")
                              .trim() || undefined
                          }
                        >
                          {col.key === "surveyType" ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="block truncate cursor-default">
                                  {renderBaseCell(
                                    col.key,
                                    survey[col.key],
                                    survey,
                                    onAddReportFile,
                                    isEditing
                                  )}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left">
                                {surveyTypeLabels[String(survey[col.key])] ??
                                  String(survey[col.key])}
                              </TooltipContent>
                            </Tooltip>
                          ) : col.key === "mapSurveyId" || col.key === "countryName" ? (
                            <TruncatedTextCell
                              text={survey[col.key] != null ? String(survey[col.key]) : ""}
                              widthPx={
                                (col.key === "mapSurveyId" ? mapSurveyIdWidth : countryNameWidth) -
                                CELL_HORIZONTAL_PADDING_PX
                              }
                            />
                          ) : (
                            renderBaseCell(
                              col.key,
                              survey[col.key],
                              survey,
                              onAddReportFile,
                              isEditing
                            )
                          )}
                        </TableCell>
                      )
                    })}
                    {showDataTopics && (
                      <TableCell
                        className={["border-l border-b", isDimmed ? "opacity-30" : ""].join(" ")}
                      >
                        {isEditing ? (
                          /* Edit mode: 2-column grid of dropdowns for each topic */
                          <div className="grid grid-cols-2 gap-1.5 w-[310px]">
                            {DATA_TOPIC_FIELDS.map(field => (
                              <div key={field.key} className="flex flex-col gap-0.5">
                                <span className="text-[10px] text-muted-foreground font-medium truncate">
                                  {field.label}
                                </span>
                                <Select
                                  value={formValues[field.key]}
                                  onValueChange={value =>
                                    handleFieldChange(field.key, value as DataTopicStatus)
                                  }
                                >
                                  <SelectTrigger
                                    className={[
                                      "h-6! p-1 text-xs w-full",
                                      formValues[field.key] ===
                                        DataTopicStatus.MICRO_DATA_EXTRACTED ||
                                      formValues[field.key] ===
                                        DataTopicStatus.REPORT_DATA_EXTRACTED
                                        ? "bg-primary/25"
                                        : "",
                                    ].join(" ")}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DATA_TOPIC_OPTIONS.map(option => (
                                      <SelectItem
                                        key={option}
                                        value={option}
                                        className={[
                                          "text-xs",
                                          option === DataTopicStatus.MICRO_DATA_EXTRACTED ||
                                          option === DataTopicStatus.REPORT_DATA_EXTRACTED
                                            ? "bg-primary/25 focus:bg-primary/35"
                                            : "",
                                        ].join(" ")}
                                      >
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(survey.dataTopics ?? []).map(topic => (
                              <Badge key={topic.name} className="bg-primary/25 text-foreground">
                                {DATA_TOPIC_LABELS[topic.name] ?? topic.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="px-3 w-0 bg-card md:sticky right-0 md:z-20 border-l border-b md:shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.15)]">
                      <div className={`flex items-center gap-1 ${isDimmed ? "opacity-30" : ""}`}>
                        {isEditing ? (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="default"
                                      size="icon"
                                      className="size-7 bg-green-500"
                                      onClick={() => handleSave(survey)}
                                      disabled={submitting}
                                    >
                                      <Check className="size-3.5" />
                                      <span className="sr-only">Save data topics</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{submitting ? "Saving…" : "Save"}</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="destructive"
                                      size="icon"
                                      className="size-7"
                                      onClick={closeEdit}
                                      disabled={submitting}
                                    >
                                      <X className="size-3.5" />
                                      <span className="sr-only">Cancel</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Cancel</TooltipContent>
                                </Tooltip>
                              </>
                        ) : (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="size-7"
                                  onClick={() => openEdit(survey, rowKey)}
                                >
                                  <Pencil className="size-3.5" />
                                  <span className="sr-only">Edit report and/or data topics</span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit report and/or data topics</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="relative size-7"
                                  onClick={() => onViewNotes(survey)}
                                >
                                  <MessageSquareText className="size-3.5" />
                                  <span className="sr-only">View notes</span>
                                  {(survey.notes?.length ?? 0) > 0 && (
                                    <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary text-primary-foreground px-1 text-[10px] font-medium leading-none">
                                      {survey.notes?.length}
                                    </span>
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View notes</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="size-7"
                                  asChild
                                >
                                  <Link to={`/survey-catalogue/${survey.mapSurveyId}`} state={{ survey }}>
                                    <ExternalLink className="size-3.5" />
                                    <span className="sr-only">Open survey</span>
                                  </Link>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Open survey</TooltipContent>
                            </Tooltip>
                          </>
                        )}
                        {isEditing && submitError && (
                          <p className="text-[10px] text-destructive max-w-[120px]" role="alert">
                            {submitError}
                          </p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
              {paddingBottom > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={totalColumnCount} style={{ height: `${paddingBottom}px` }} />
                </tr>
              )}
            </>
          )}
        </TableBody>
      </Table>
      </div>
    </div>
  )
}

export default SurveyCompactTable
